import { useEffect, useRef } from 'react';

import { addUFOCustomData } from '@atlaskit/react-ufo/add-ufo-custom-data';
import { getActiveInteraction } from '@atlaskit/react-ufo/interaction-metrics';

import { fg } from '@confluence/feature-gating';
import { recordNonContentAppKeys } from '@confluence/browser-metrics/entry-points/NonContentAppKeys.ts';
import { FORGE_MODULE_BYLINE } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import type { ForgeUIContentBylineItemExtensionType } from '@confluence/forge-ui/entry-points/types.ts';
import { useExtensionList } from '@confluence/forge-ui/entry-points/useExtensionList.ts';
import { SYSTEM_CONTENT_BYLINE } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { useWebItemLocation } from '@confluence/web-item-location/entry-points/useWebItemLocation.ts';
import type { useWebItemLocationWebItemType as WebItem } from '@confluence/web-item-location/entry-points/useWebItemLocation.ts';

import { getLoadedBylineMetrics, loadBylineMetrics } from './loadBylineMetrics.ts';

// Hook to grab byline app info for view and live pages.
export const useByLineApps = (
	contentId: string,
): {
	hasApps: boolean;
	connectItems: WebItem[];
	loadingConnectItems: boolean;
	forgeItems: ForgeUIContentBylineItemExtensionType[];
	loadingForgeItems: boolean;
	connectError: Error | undefined;
	forgeError: Error | undefined;
} => {
	const {
		webItems: connectItems,
		loading: loadingConnectItems,
		error: connectError,
	} = useWebItemLocation({
		contentId,
		location: SYSTEM_CONTENT_BYLINE,
	});

	const {
		extensions: forgeItems,
		loading: loadingForgeItems,
		error: forgeError,
	} = useExtensionList({
		moduleType: FORGE_MODULE_BYLINE,
	});

	// Instrument the presence of non-content 3P byline apps (connect + forge) so
	// their keys are folded into the page-load `connectAppKeys` telemetry.
	useEffect(() => {
		recordNonContentAppKeys('byline', [
			...(connectItems?.map((item) => item.completeKey) ?? []),
			...(forgeItems?.map((item) => item.id) ?? []),
		]);
	}, [connectItems, forgeItems]);

	const lastDiscovery = useRef<string | undefined>(undefined);
	useEffect(() => {
		if (!fg('confluence_byline_cls_telemetry')) return;
		const interaction = getActiveInteraction();
		let cancelled = false;
		const record = (metrics: ReturnType<typeof getLoadedBylineMetrics>) => {
			if (
				cancelled ||
				!metrics ||
				!fg('confluence_byline_cls_telemetry') ||
				getActiveInteraction() !== interaction ||
				interaction?.end
			)
				return;
			const data = metrics.getByLineAppMetrics({
				connectItems,
				forgeItems,
				loadingConnectItems,
				loadingForgeItems,
				connectError,
				forgeError,
			});
			const signature = JSON.stringify([contentId, data]);
			if (lastDiscovery.current === signature) return;
			lastDiscovery.current = signature;
			// Bounded configuration-only fields; no app IDs, titles, property keys, or error text.
			addUFOCustomData(data);
		};
		const loaded = getLoadedBylineMetrics();
		if (loaded) record(loaded);
		else void loadBylineMetrics().then(record);
		return () => {
			cancelled = true;
		};
	}, [
		contentId,
		connectItems,
		forgeItems,
		loadingConnectItems,
		loadingForgeItems,
		connectError,
		forgeError,
	]);

	return {
		hasApps: connectItems?.length > 0 || forgeItems?.length > 0,
		connectItems,
		loadingConnectItems,
		connectError,
		forgeItems,
		loadingForgeItems,
		forgeError,
	};
};
